import {
  Alert,
  Box,
  Button,
  FormField,
  Input,
  Modal,
  SpaceBetween,
  StatusIndicator,
} from '@cloudscape-design/components';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { sendSelectedReports as sendSelectedReportsApi } from '../../common/api/reports';
import type { StatusIndicatorProps } from '@cloudscape-design/components';
import type { CSSProperties } from 'react';
import type {
  PlaceReportWithContacts,
  SendSelectedReportChannelResult,
  SendSelectedReportResult,
  SendSelectedReportsRequest,
  SendSelectedReportsResponse,
} from '../../common/types';

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const identifierWrapStyle: CSSProperties = {
  overflowWrap: 'anywhere',
};
const detailWrapStyle: CSSProperties = {
  overflowWrap: 'break-word',
  wordBreak: 'normal',
};
const channelDetailStyle: CSSProperties = {
  overflowWrap: 'break-word',
  wordBreak: 'normal',
};

export type SendSelectedReportsValidation = {
  email?: string;
  phone?: string;
  form?: string;
};

export function validateSendSelectedReportsInputs(
  email: string,
  phone: string,
): SendSelectedReportsValidation {
  const trimmedEmail = email.trim();
  const trimmedPhone = phone.trim();
  const errors: SendSelectedReportsValidation = {};

  if (!trimmedEmail && !trimmedPhone) {
    errors.form = 'Enter an email address, a phone number, or both.';
  }
  if (trimmedEmail && !EMAIL_PATTERN.test(trimmedEmail)) {
    errors.email = 'Enter a valid email address.';
  }
  if (trimmedPhone) {
    const digits = trimmedPhone.replace(/\D/g, '');
    if (digits.length < 10 || digits.length > 15) {
      errors.phone = 'Enter a valid phone number.';
    }
  }
  return errors;
}

export type SendSelectedReportsModalProps = {
  visible: boolean;
  selectedReports: PlaceReportWithContacts[];
  onDismiss: () => void;
  onSentAll: () => void;
  sendReports?: (body: SendSelectedReportsRequest) => Promise<SendSelectedReportsResponse>;
};

function channelLabel(channel: string): string {
  if (channel === 'sms') return 'SMS';
  if (channel === 'email') return 'Email';
  return 'Report';
}

function statusType(status: string): StatusIndicatorProps.Type {
  if (status === 'success') return 'success';
  if (status === 'forbidden') return 'warning';
  return 'error';
}

function isSuccessResult(result: SendSelectedReportResult): boolean {
  return result.channels.length > 0 && result.channels.every((channel) => channel.status === 'success');
}

function hasSuccessfulChannel(result: SendSelectedReportsResponse): boolean {
  return result.results.some((item) => item.channels.some((channel) => channel.status === 'success'));
}

function hasFailedChannel(result: SendSelectedReportsResponse): boolean {
  return result.results.some((item) => item.channels.some((channel) => channel.status !== 'success'));
}

function hasErrors(errors: SendSelectedReportsValidation): boolean {
  return Boolean(errors.email || errors.phone || errors.form);
}

function ResultChannel({ channel }: { channel: SendSelectedReportChannelResult }) {
  return (
    <>
      <StatusIndicator type={statusType(channel.status)}>{channelLabel(channel.channel)}</StatusIndicator>
      {channel.detail ? <span style={channelDetailStyle}>: {channel.detail}</span> : null}
    </>
  );
}

export function SendSelectedReportsModal({
  visible,
  selectedReports,
  onDismiss,
  onSentAll,
  sendReports = sendSelectedReportsApi,
}: SendSelectedReportsModalProps) {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState<SendSelectedReportsValidation>({});
  const [result, setResult] = useState<SendSelectedReportsResponse | null>(null);

  useEffect(() => {
    if (!visible) {
      setEmail('');
      setPhone('');
      setErrors({});
      setResult(null);
    }
  }, [visible]);

  const mutation = useMutation({
    mutationFn: sendReports,
  });

  const selectedNames = useMemo(
    () =>
      selectedReports
        .map((report) => report.place?.name ?? report.uuid)
        .slice(0, 3)
        .join(', '),
    [selectedReports],
  );

  const allSent = result?.results.every(isSuccessResult) ?? false;
  const partialFailure = Boolean(result && !allSent && hasSuccessfulChannel(result) && hasFailedChannel(result));
  const totalSelected = selectedReports.length;

  const onSubmit = async () => {
    const nextErrors = validateSendSelectedReportsInputs(email, phone);
    if (totalSelected === 0) {
      nextErrors.form = 'Select at least one scorecard.';
    }
    setErrors(nextErrors);
    if (hasErrors(nextErrors)) return;

    const body: SendSelectedReportsRequest = {
      report_uuids: selectedReports.map((report) => report.uuid),
      ...(email.trim() ? { email: email.trim() } : {}),
      ...(phone.trim() ? { phone_number: phone.trim() } : {}),
    };
    let response: SendSelectedReportsResponse;
    try {
      response = await mutation.mutateAsync(body);
    } catch {
      return;
    }
    setResult(response);
    if (response.results.every(isSuccessResult)) {
      onSentAll();
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={mutation.isPending ? () => undefined : onDismiss}
      header={`Send ${totalSelected} selected scorecard${totalSelected === 1 ? '' : 's'}`}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={onDismiss} disabled={mutation.isPending}>
              Close
            </Button>
            <Button
              variant="primary"
              onClick={onSubmit}
              loading={mutation.isPending}
              disabled={totalSelected === 0 || mutation.isPending}
            >
              Send
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box variant="p" color="text-body-secondary">
          {totalSelected > 0
            ? `Selected: ${selectedNames}${totalSelected > 3 ? `, and ${totalSelected - 3} more` : ''}.`
            : 'Select one or more scorecards from the table before sending.'}
        </Box>

        {errors.form && <Alert type="error">{errors.form}</Alert>}
        {mutation.error && (
          <Alert type="error" header="Send failed">
            {mutation.error instanceof Error ? mutation.error.message : 'Unable to send selected scorecards.'}
          </Alert>
        )}
        {result && allSent && (
          <Alert type="success" header="Scorecards sent">
            Every selected channel completed successfully.
          </Alert>
        )}
        {result && partialFailure && (
          <Alert type="warning" header="Some sends failed">
            Review the failed rows below before retrying.
          </Alert>
        )}
        {result && !partialFailure && !allSent && (
          <Alert type="error" header="No sends completed">
            Review the failed rows below before retrying.
          </Alert>
        )}

        <SpaceBetween size="s">
          <FormField label="Email" errorText={errors.email}>
            <Input
              value={email}
              type="email"
              autoComplete="email"
              placeholder="recipient@example.com"
              disabled={mutation.isPending}
              onChange={({ detail }) => {
                setEmail(detail.value);
                if (errors.email || errors.form) setErrors({});
              }}
            />
          </FormField>
          <FormField label="Phone" errorText={errors.phone}>
            <Input
              value={phone}
              autoComplete="tel"
              placeholder="+1 972 793 2587"
              disabled={mutation.isPending}
              onChange={({ detail }) => {
                setPhone(detail.value);
                if (errors.phone || errors.form) setErrors({});
              }}
            />
          </FormField>
        </SpaceBetween>

        {result && (
          <SpaceBetween size="xs">
            {result.results.map((item) => (
              <Box key={item.report_uuid}>
                <SpaceBetween size="xxxs">
                  <div style={identifierWrapStyle}>
                    <Box variant="strong">{item.place_name ?? item.report_uuid}</Box>
                  </div>
                  <SpaceBetween size="xxs">
                    {item.channels.map((channel) => (
                      <div key={`${item.report_uuid}-${channel.channel}`} style={detailWrapStyle}>
                        <ResultChannel channel={channel} />
                      </div>
                    ))}
                  </SpaceBetween>
                </SpaceBetween>
              </Box>
            ))}
          </SpaceBetween>
        )}
      </SpaceBetween>
    </Modal>
  );
}
